import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { certificateService } from '@/services/certificate/certificate.service';
import { formService } from '@/services/form/form.service';
import { useServeFileByUrl } from '@/hooks/upload/use-file';
import type { CertificateFieldMapping, CertificateFieldType } from '@/types/certificate.types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, X, ZoomIn, ZoomOut, Maximize } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { ResizableField } from './resizable-field';
import { FieldConfigurationModal } from './field-configuration-modal';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface CertificateFieldMapperProps {
  templateId?: string;
  templateFileUrl: string;
  processId: string;
  onMappingsChange?: (mappings: CertificateFieldMapping[]) => void;
  refreshKey?: number;
}

const getFieldTypes = (t: any): Array<{ value: CertificateFieldType; label: string }> => [
  { value: 'USER_PROFILE', label: t('certificate.fieldTypes.userProfile') },
  { value: 'CERT_NUMBER', label: t('certificate.fieldTypes.certificateNumber') },
  { value: 'ISSUE_DATE', label: t('certificate.fieldTypes.issueDate') },
  { value: 'EXPIRY_DATE', label: t('certificate.fieldTypes.expiryDate') },
  { value: 'SUBMISSION_DATE', label: t('certificate.fieldTypes.submissionDate') },
  { value: 'QR_CODE', label: t('certificate.fieldTypes.qrCode') },
  { value: 'CUSTOM', label: t('certificate.fieldTypes.custom') },
];

const CertificateFieldMapper: React.FC<CertificateFieldMapperProps> = ({
  templateId,
  templateFileUrl,
  processId,
  onMappingsChange,
  refreshKey,
}) => {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [mappings, setMappings] = useState<CertificateFieldMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingMapping, setEditingMapping] = useState<CertificateFieldMapping | null>(null);
  const [isClickMode, setIsClickMode] = useState(false);
  const [pendingFieldType, setPendingFieldType] = useState<CertificateFieldType | null>(null);
  const [localMappings, setLocalMappings] = useState<CertificateFieldMapping[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [drawCurrent, setDrawCurrent] = useState<{ x: number; y: number } | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const localMappingsRef = useRef<CertificateFieldMapping[]>(localMappings);
  useEffect(() => {
    localMappingsRef.current = localMappings;
  }, [localMappings]);
  const [pageDimensions, setPageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [pdfLibDimensions, setPdfLibDimensions] = useState<{ width: number; height: number } | null>(null);
  const [pixelToPointRatio, setPixelToPointRatio] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const [questionLabelMap, setQuestionLabelMap] = useState<Record<string, string>>({});
  const [zoom, setZoom] = useState<number>(1);
  const [selectedMappingId, setSelectedMappingId] = useState<string | null>(null);
  const [copiedMapping, setCopiedMapping] = useState<CertificateFieldMapping | null>(null);

  // Use the hook to get PDF blob URL (consistent with profile/upload pattern)
  const { 
    data: pdfUrl, 
    isLoading: isPdfLoading, 
    error: pdfError 
  } = useServeFileByUrl(templateFileUrl);

  // Load PDF dimensions with pdf-lib when PDF URL is available
  useEffect(() => {
    if (!pdfUrl) return;

    const loadPdfDimensions = async () => {
      try {
        // Fetch PDF bytes from blob URL to get dimensions
        const response = await fetch(pdfUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }

        const pdfBytes = await response.arrayBuffer();
        if (pdfBytes.byteLength === 0) {
          throw new Error(t('certificate.fieldMapping.pdfFileIsEmpty'));
        }

        // Load PDF with pdf-lib to get page dimensions in PDF points
        const pdf = await PDFDocument.load(pdfBytes);
        const pageCount = pdf.getPageCount();

        if (pageCount === 0) {
          throw new Error(t('certificate.fieldMapping.pdfHasNoPages'));
        }

        // Get page dimensions from pdf-lib (first page) - these are in PDF points
        const page = pdf.getPage(0);
        const { width, height } = page.getSize();

        setPdfLibDimensions({ width, height });
        console.log('[PDF] pdf-lib dimensions:', { width, height });
      } catch (pdfLibError) {
        console.error('Error loading PDF with pdf-lib:', pdfLibError);
        // Fallback: will use react-pdf dimensions with a warning
        toast.warning(t('certificate.fieldMapping.couldNotLoadPdfDimensions'));
      }
    };

    loadPdfDimensions();
  }, [pdfUrl, t]);

  // Load mappings when template ID or refreshKey changes (refreshKey increments after parent saves)
  useEffect(() => {
    if (templateId) {
      loadMappings();
    }
  }, [templateId, refreshKey]);

  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth - 32; // Account for padding
        setContainerWidth(Math.max(400, width));
      }
    };

    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, []);



  useEffect(() => {
    const customMappings = mappings.filter((m) => m.fieldType === 'CUSTOM' && m.sourceFormId && m.sourceQuestionId);
    if (customMappings.length === 0) return;

    const uniqueFormIds = [...new Set(customMappings.map((m) => m.sourceFormId as string))];

    const buildMap = async () => {
      const map: Record<string, string> = {};
      await Promise.all(
        uniqueFormIds.map(async (formId) => {
          try {
            const formData = await formService.getFormById(formId);
            const design = formData?.design || formData?.data?.design;
            if (!design) return;
            const sections: any[] = Array.isArray(design) ? design : design.sections || [];
            sections.forEach((section: any) => {
              (section.questions || []).forEach((q: any) => {
                if (q.id && q.label) map[q.id] = q.label;
              });
            });
          } catch {
            // silently skip if a form can't be fetched
          }
        }),
      );
      setQuestionLabelMap((prev) => ({ ...prev, ...map }));
    };

    buildMap();
  }, [mappings]);

  const loadMappings = async () => {
    if (!templateId) return;

    try {
      setLoading(true);
      const fieldMappings = await certificateService.getFieldMappings(templateId);
      setMappings(fieldMappings);
      setLocalMappings(fieldMappings);
      onMappingsChange?.(fieldMappings);
    } catch (error: any) {
      if (error.response?.status !== 404) {
        toast.error(t('certificate.fieldMapping.failedToLoadFieldMappings'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isClickMode || !pendingFieldType) return;
    
    // Don't start drawing if clicking on existing fields
    if ((event.target as HTMLElement).closest('.resizable-field') || 
        (event.target as HTMLElement).closest('.react-resizable-handle')) {
      return;
    }

    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect || !pageDimensions) return;

    // Get click position relative to the page element
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Calculate the actual PDF page dimensions on screen
    const screenPageWidth = rect.width;
    const screenPageHeight = (pageDimensions.height / pageDimensions.width) * screenPageWidth;

    // Ensure coordinates are within the actual PDF page bounds
    const clampedX = Math.max(0, Math.min(x, screenPageWidth));
    const clampedY = Math.max(0, Math.min(y, screenPageHeight));

    setDrawStart({ x: clampedX, y: clampedY });
    setDrawCurrent({ x: clampedX, y: clampedY });
    setIsDrawing(true);
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePageMouseMove = useCallback((event: MouseEvent) => {
    if (!isDrawing || !drawStart || !pageRef.current || !pageDimensions || !pendingFieldType) return;

    const rect = pageRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const screenPageWidth = rect.width;
    const screenPageHeight = (pageDimensions.height / pageDimensions.width) * screenPageWidth;
    let currentX = Math.max(0, Math.min(x, screenPageWidth));
    let currentY = Math.max(0, Math.min(y, screenPageHeight));

    if (pendingFieldType === 'QR_CODE') {
      const width = Math.abs(currentX - drawStart.x);
      const height = Math.abs(currentY - drawStart.y);
      const size = Math.max(width, height);
      const deltaX = currentX - drawStart.x;
      const deltaY = currentY - drawStart.y;
      
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        currentY = drawStart.y + (deltaX > 0 ? size : -size);
      } else {
        currentX = drawStart.x + (deltaY > 0 ? size : -size);
      }
      
      currentX = Math.max(0, Math.min(currentX, screenPageWidth));
      currentY = Math.max(0, Math.min(currentY, screenPageHeight));
    }

    setDrawCurrent({ x: currentX, y: currentY });
  }, [isDrawing, drawStart, pageDimensions, pendingFieldType]);

  const handlePageMouseUpGlobal = useCallback((event: MouseEvent) => {
    if (!isDrawing || !drawStart || !drawCurrent || !pageRef.current || !pageDimensions || !pendingFieldType) {
      setIsDrawing(false);
      setDrawStart(null);
      setDrawCurrent(null);
      return;
    }

    // Use pdf-lib dimensions if available, otherwise fallback to react-pdf dimensions
    const dimensions = pdfLibDimensions || pageDimensions;
    const ratio = pixelToPointRatio || (dimensions.width / pageDimensions.width);

    const rect = pageRef.current.getBoundingClientRect();
    const screenPageWidth = rect.width;
    const screenPageHeight = (pageDimensions.height / pageDimensions.width) * screenPageWidth;

    // Calculate drawn area (in screen pixels)
    const minX = Math.min(drawStart.x, drawCurrent.x);
    const minY = Math.min(drawStart.y, drawCurrent.y);
    const maxX = Math.max(drawStart.x, drawCurrent.x);
    const maxY = Math.max(drawStart.y, drawCurrent.y);

    // Calculate width and height (in screen pixels)
    let width = maxX - minX;
    let height = maxY - minY;

    // Minimum size check
    const minSize = 20;
    if (width < minSize) width = minSize;
    if (height < minSize) height = minSize;

    // For QR codes, ensure square (1:1)
    if (pendingFieldType === 'QR_CODE') {
      const size = Math.max(width, height);
      width = size;
      height = size;
    }
    // For photo fields (USER_PROFILE with photo), ensure 3:4 aspect ratio (passport standard)
    // Note: userProfileField defaults to 'fullName', but we'll enforce ratio when it's changed to 'photo'
    // This will be handled in the resizable component

    // Ensure within bounds
    const finalMinX = Math.max(0, Math.min(minX, screenPageWidth - width));
    const finalMinY = Math.max(0, Math.min(minY, screenPageHeight - height));

    // Convert screen coordinates (pixels, top-left origin) to PDF points (bottom-left origin)
    // X coordinate: multiply by ratio
    const pdfX = finalMinX * ratio;
    // Y coordinate: flip from top-left to bottom-left origin
    const pdfY = dimensions.height - (finalMinY * ratio);
    // Width and height: multiply by ratio
    const pdfWidth = width * ratio;
    const pdfHeight = height * ratio;

    // Ensure PDF coordinates are within bounds
    const finalPdfX = Math.max(0, Math.min(pdfX, dimensions.width));
    const finalPdfY = Math.max(0, Math.min(pdfY, dimensions.height));
    // Ensure minimum dimensions are stored (at least 20 points)
    const finalPdfWidth = Math.max(20, Math.min(pdfWidth, dimensions.width - finalPdfX));
    const finalPdfHeight = Math.max(20, Math.min(pdfHeight, dimensions.height - finalPdfY));

    const fontSize = 16;

    const newMapping: CertificateFieldMapping = {
      id: `temp-${Date.now()}`,
      certificateTemplateId: templateId || '',
      fieldType: pendingFieldType,
      x: finalPdfX,
      y: finalPdfY,
      width: finalPdfWidth,
      height: finalPdfHeight,
      fontSize: fontSize,
      fontFamily: 'Helvetica',
      color: '#000000',
      alignment: 'left',
      label: null,
      sourceFormId: null,
      sourceQuestionId: null,
      userProfileField: pendingFieldType === 'USER_PROFILE' ? 'fullName' : null,
      pageNumber: pageNumber,
      isBold: false,
      isItalic: false,
      isUnderlined: false,
      textTransform: null,
      opacity: 1,
      shrinkToFit: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    setLocalMappings((prev) => {
      const updated = [...prev, newMapping];
      return updated;
    });
    onMappingsChange?.([...localMappingsRef.current, newMapping]);
    
    setIsClickMode(false);
    setPendingFieldType(null);
    setIsDrawing(false);
    setDrawStart(null);
    setDrawCurrent(null);
  }, [isDrawing, drawStart, drawCurrent, pageDimensions, pdfLibDimensions, pixelToPointRatio, pendingFieldType, templateId, onMappingsChange, t]);
  useEffect(() => {
    if (isDrawing) {
      document.addEventListener('mousemove', handlePageMouseMove);
      document.addEventListener('mouseup', handlePageMouseUpGlobal);
      return () => {
        document.removeEventListener('mousemove', handlePageMouseMove);
        document.removeEventListener('mouseup', handlePageMouseUpGlobal);
      };
    }
  }, [isDrawing, handlePageMouseMove, handlePageMouseUpGlobal]);


  // Paste a copy of the copied field, offset slightly so it does not hide the original
  const handlePasteField = () => {
    if (!copiedMapping) return;

    const dimensions = pdfLibDimensions || pageDimensions;
    const OFFSET = 10;
    const maxX = dimensions ? dimensions.width : Number.MAX_SAFE_INTEGER;
    const maxY = dimensions ? dimensions.height : Number.MAX_SAFE_INTEGER;

    const duplicate: CertificateFieldMapping = {
      ...copiedMapping,
      id: `temp-${Date.now()}`,
      x: Math.max(0, Math.min(copiedMapping.x + OFFSET, maxX)),
      y: Math.max(0, Math.min(copiedMapping.y - OFFSET, maxY)),
      pageNumber,
    };

    setLocalMappings((prev) => [...prev, duplicate]);
    setSelectedMappingId(duplicate.id);
    toast.success(t('certificate.fieldMapping.fieldPasted'));
  };

  // Field overlays are positioned from the rendered page's bounding rect, which is
  // read during render. Re-render whenever that box actually changes size so zoom
  // and window resizes reposition fields instead of leaving them on stale numbers.
  const [, setPageRectVersion] = useState(0);
  useEffect(() => {
    const element = pageRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setPageRectVersion((v) => v + 1));
    observer.observe(element);
    return () => observer.disconnect();
  }, [pdfUrl, pageNumber]);

  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 3;
  const ZOOM_STEP = 0.25;

  const adjustZoom = (delta: number) =>
    setZoom((current) =>
      Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number((current + delta).toFixed(2)))),
    );

  const NUDGE_STEP = 1;
  const NUDGE_STEP_LARGE = 10;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isDrawing) {
          setIsDrawing(false);
          setDrawStart(null);
          setDrawCurrent(null);
          setIsClickMode(false);
          setPendingFieldType(null);
        }
        setSelectedMappingId(null);
        return;
      }

      // Never steal keys from a field the user is typing into
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return;
      }

      const selected = localMappingsRef.current.find(
        (m) => m.id === selectedMappingId,
      );
      if (!selected) return;

      const isCopy = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'c';
      const isPaste = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'v';

      if (isCopy) {
        event.preventDefault();
        setCopiedMapping(selected);
        toast.success(t('certificate.fieldMapping.fieldCopied'));
        return;
      }

      if (isPaste) {
        event.preventDefault();
        handlePasteField();
        return;
      }

      const arrows: Record<string, { dx: number; dy: number }> = {
        // PDF space has a bottom-left origin, so "up" increases y
        ArrowUp: { dx: 0, dy: 1 },
        ArrowDown: { dx: 0, dy: -1 },
        ArrowLeft: { dx: -1, dy: 0 },
        ArrowRight: { dx: 1, dy: 0 },
      };
      const delta = arrows[event.key];
      if (!delta) return;

      event.preventDefault();
      const step = event.shiftKey ? NUDGE_STEP_LARGE : NUDGE_STEP;
      const dimensions = pdfLibDimensions || pageDimensions;
      const maxX = dimensions ? dimensions.width : Number.MAX_SAFE_INTEGER;
      const maxY = dimensions ? dimensions.height : Number.MAX_SAFE_INTEGER;

      handleUpdateMapping(selected.id, {
        x: Math.max(0, Math.min(selected.x + delta.dx * step, maxX)),
        y: Math.max(0, Math.min(selected.y + delta.dy * step, maxY)),
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawing, selectedMappingId, copiedMapping, pageDimensions, pdfLibDimensions, pageNumber, t]);

  // This function is no longer used for direct backend saves
  // All saves now go through handleSaveAllChanges
  // Keeping for backward compatibility
  const saveMapping = async (mappingData: Partial<CertificateFieldMapping>) => {
    if (!templateId) {
      toast.error(t('certificate.fieldMapping.pleaseSaveTemplateFirstError'));
      return;
    }

    // Just add to local mappings - user needs to click "Save Changes" to save to backend
    const newMapping: CertificateFieldMapping = {
      id: `temp-${Date.now()}`,
      certificateTemplateId: templateId,
      fieldType: mappingData.fieldType!,
      x: mappingData.x!,
      y: mappingData.y!,
      width: mappingData.width ?? null,
      height: mappingData.height ?? null,
      fontSize: mappingData.fontSize ?? 16,
      fontFamily: mappingData.fontFamily ?? 'Helvetica',
      color: mappingData.color ?? '#000000',
      alignment: mappingData.alignment ?? 'left',
      sourceFormId: mappingData.sourceFormId ?? null,
      sourceQuestionId: mappingData.sourceQuestionId ?? null,
      label: mappingData.label ?? null,
      userProfileField: mappingData.userProfileField ?? (mappingData.fieldType === 'USER_PROFILE' ? 'fullName' : null),
      pageNumber: mappingData.pageNumber ?? pageNumber,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    const updatedMappings = [...localMappings, newMapping];
    setLocalMappings(updatedMappings);
    onMappingsChange?.(updatedMappings);
  };

  const handleDeleteMapping = (id: string) => {
    const updatedMappings = localMappings.filter((m) => m.id !== id);
    setLocalMappings(updatedMappings);
    onMappingsChange?.(updatedMappings);
  };

  const handleUpdateMapping = (id: string, updates: Partial<CertificateFieldMapping>) => {
    const updatedMappings = localMappings.map((mapping) =>
      mapping.id === id ? { ...mapping, ...updates } : mapping
    );
    setLocalMappings(updatedMappings);
    onMappingsChange?.(updatedMappings);
  };

  const handleSaveAllChanges = async () => {
    if (!templateId) return;

    try {
      setSaving(true);
      
      // Get current backend mappings to compare
      const backendMappings = await certificateService.getFieldMappings(templateId);
      const backendMappingIds = new Set(backendMappings.map(m => m.id));
      const localMappingIds = new Set(localMappings.map(m => m.id).filter(id => !id.startsWith('temp-')));
      
      // Find mappings to delete (exist in backend but not in local)
      const mappingsToDelete = backendMappings.filter(m => !localMappingIds.has(m.id));
      
      // Delete removed mappings
      for (const mappingToDelete of mappingsToDelete) {
        await certificateService.deleteFieldMapping(mappingToDelete.id);
      }
      
      // Save all local mappings to backend
      for (const mapping of localMappings) {
        // Check if this is a new mapping (temp id) or an update
        if (mapping.id && !mapping.id.startsWith('temp-')) {
          // Update existing mapping
          await certificateService.updateFieldMapping(mapping.id, {
            x: mapping.x,
            y: mapping.y,
            width: mapping.width ?? null,
            height: mapping.height ?? null,
            fontSize: mapping.fontSize ?? null,
            fontFamily: mapping.fontFamily ?? null,
            color: mapping.color ?? null,
            alignment: mapping.alignment ?? null,
            label: mapping.label ?? null,
            sourceFormId: mapping.sourceFormId ?? null,
            sourceQuestionId: mapping.sourceQuestionId ?? null,
            userProfileField: mapping.userProfileField ?? null,
            pageNumber: mapping.pageNumber ?? 1,
            isBold: mapping.isBold ?? false,
            isItalic: mapping.isItalic ?? false,
            isUnderlined: mapping.isUnderlined ?? false,
            textTransform: mapping.textTransform ?? null,
            opacity: mapping.opacity ?? null,
            shrinkToFit: mapping.shrinkToFit ?? false,
          });
        } else {
          // Create new mapping (either no id or temp id)
      await certificateService.createFieldMapping({
        certificateTemplateId: templateId,
            fieldType: mapping.fieldType,
            x: mapping.x,
            y: mapping.y,
            width: mapping.width ?? null,
            height: mapping.height ?? null,
            fontSize: mapping.fontSize ?? null,
            fontFamily: mapping.fontFamily ?? null,
            color: mapping.color ?? null,
            alignment: mapping.alignment ?? null,
            label: mapping.label ?? null,
            sourceFormId: mapping.sourceFormId ?? null,
            sourceQuestionId: mapping.sourceQuestionId ?? null,
            userProfileField: mapping.userProfileField ?? null,
            pageNumber: mapping.pageNumber ?? 1,
            isBold: mapping.isBold ?? false,
            isItalic: mapping.isItalic ?? false,
            isUnderlined: mapping.isUnderlined ?? false,
            textTransform: mapping.textTransform ?? null,
            opacity: mapping.opacity ?? null,
            shrinkToFit: mapping.shrinkToFit ?? false,
      });
        }
      }

      await loadMappings();
      toast.success(t('certificate.fieldMapping.allChangesSaved'));
    } catch (error: any) {
      console.error('Error saving changes:', error);
      toast.error(error.response?.data?.message || t('certificate.fieldMapping.failedToSaveChanges'));
    } finally {
      setSaving(false);
    }
  };

  const getPlaceholderText = (mapping: CertificateFieldMapping): string => {
    switch (mapping.fieldType) {
      case 'USER_PROFILE':
        if (mapping.userProfileField === 'photo') {
          return '[Photo]';
        }
        if (mapping.userProfileField === 'firstName') {
          return mapping.label || 'John';
        }
        if (mapping.userProfileField === 'lastName') {
          return mapping.label || 'Doe';
        }
        if (mapping.userProfileField === 'fullName') {
          return mapping.label || 'John Doe';
        }
        if (mapping.userProfileField === 'email') {
          return mapping.label || 'john.doe@example.com';
        }
        return mapping.label || '[User Profile]';
      case 'CERT_NUMBER':
        return mapping.label || 'CERT-2024-001';
      case 'ISSUE_DATE':
        return mapping.label || '2024-01-15';
      case 'EXPIRY_DATE':
        return mapping.label || '2025-01-15';
      case 'SUBMISSION_DATE':
        return mapping.label || '2024-01-10';
      case 'QR_CODE':
        return '';
      case 'CUSTOM':
        if (mapping.label) {
          return mapping.label;
        }
        if (mapping.sourceQuestionId) {
          return questionLabelMap[mapping.sourceQuestionId] || `[Form Answer: ${mapping.sourceQuestionId}]`;
        }
        return '[Custom Field]';
      default:
        return '[Field]';
    }
  };

  const startClickMode = (fieldType: CertificateFieldType) => {
    if (!templateId) {
      toast.error(t('certificate.fieldMapping.pleaseSaveTemplateFirstError'));
      return;
    }
    setIsClickMode(true);
    setPendingFieldType(fieldType);
    toast.info(t('certificate.fieldMapping.clickToPlaceField'));
  };

  const cancelClickMode = () => {
    setIsClickMode(false);
    setPendingFieldType(null);
  };

  const getFieldTypeLabel = (type: string | CertificateFieldType): string => {
    return getFieldTypes(t).find((ft) => ft.value === type)?.label || String(type);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('certificate.fieldMapping.title')}</CardTitle>
          <CardDescription>
            {t('certificate.fieldMapping.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!templateId ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                {t('certificate.fieldMapping.pleaseSaveTemplateFirst')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>{t('certificate.fieldMapping.addField')}</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {getFieldTypes(t).map((fieldType) => (
                    <Button
                      key={fieldType.value}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => startClickMode(fieldType.value)}
                      disabled={isClickMode}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {fieldType.label}
                    </Button>
                  ))}
                </div>
                </div>
                {isClickMode && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-blue-800">
                      {t('certificate.fieldMapping.drawingModeActive')}{' '}
                        <strong>{getFieldTypeLabel(pendingFieldType!)}</strong>
                      {pendingFieldType === 'QR_CODE' && (
                        <span className="ml-2 text-[#001A55]/70">{t('certificate.fieldMapping.maintainsSquareAspectRatio')}</span>
                      )}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={cancelClickMode}
                      >
                      <X className="h-4 w-4 mr-2" />
                      {t('certificate.fieldMapping.cancel')}
                      </Button>
                    </div>
                  </div>
                )}

              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium">{t('certificate.fieldMapping.pdfPreview')}</h3>
                </div>

                {pdfUrl ? (
                  <div
                    ref={containerRef}
                    className="w-full overflow-auto max-h-[800px]"
                    style={{ minHeight: '400px' }}
                  >
                    <div
                      className="relative border border-gray-300 rounded bg-white mx-auto"
                      style={{ 
                        width: 'fit-content',
                        maxWidth: '100%',
                        display: 'inline-block'
                      }}
                    >
                      {isPdfLoading && (
                        <div className="p-8 text-center">
                          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                          <p>{t('certificate.fieldMapping.loadingPdf')}</p>
                        </div>
                      )}
                      <div
                        ref={pageRef}
                        onMouseDown={handlePageMouseDown}
                        className="relative"
                        style={{ 
                          display: 'inline-block',
                          cursor: isClickMode ? 'crosshair' : 'default'
                        }}
                  >
                    {pdfUrl ? (
                    <Document
                      file={pdfUrl}
                        loading={<div className="p-8 text-center">{t('certificate.fieldMapping.loadingPdf')}</div>}
                        onLoadSuccess={({ numPages }) => {
                          setNumPages(numPages);
                        }}
                        onLoadError={(error) => {
                          console.error('Error loading PDF:', error);
                          toast.error(t('certificate.fieldMapping.failedToLoadPdfTemplate'));
                        }}
                        error={
                          <div className="p-8 text-center text-red-500">
                            {t('certificate.fieldMapping.failedToLoadPdf')}
                          </div>
                        }
                    >
                      <Page
                        pageNumber={pageNumber}
                          width={containerWidth * zoom}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                          loading={
                            <div className="p-8 text-center">
                              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                            </div>
                          }
                          onLoadSuccess={(page) => {
                            if (page) {
                              const renderedDimensions = {
                            width: page.width,
                            height: page.height,
                              };
                              setPageDimensions(renderedDimensions);
                              
                              // Calculate pixel-to-point ratio if pdf-lib dimensions are available
                              if (pdfLibDimensions) {
                                const ratio = pdfLibDimensions.width / renderedDimensions.width;
                                setPixelToPointRatio(ratio);
                                console.log('[PDF] Pixel-to-point ratio:', ratio);
                                console.log('[PDF] react-pdf dimensions:', renderedDimensions);
                                console.log('[PDF] pdf-lib dimensions:', pdfLibDimensions);
                              }
                            }
                          }}
                          onLoadError={(error) => {
                            console.error('Error loading PDF page:', error);
                            toast.error(t('certificate.fieldMapping.failedToLoadPdfPage'));
                        }}
                      />
                    </Document>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        {isPdfLoading ? (
                          <div>
                            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                            <p>{t('certificate.fieldMapping.loadingPdf')}</p>
                          </div>
                        ) : pdfError ? (
                          <div>
                            <p className="text-red-500">{t('certificate.fieldMapping.failedToLoadPdfTemplate')}</p>
                          </div>
                        ) : (
                          <p>{t('certificate.fieldMapping.noPdfAvailable')}</p>
                        )}
                      </div>
                    )}
                      </div>

                    {/* Drawing selection rectangle overlay */}
                    {isDrawing && drawStart && drawCurrent && (
                      <div
                        className="absolute border-2 border-dashed pointer-events-none z-10"
                        style={{
                          borderColor: pendingFieldType === 'QR_CODE' ? '#10b981' : '#3b82f6',
                          backgroundColor: pendingFieldType === 'QR_CODE'
                            ? 'rgba(0, 26, 85, 0.08)'
                            : 'rgba(59, 130, 246, 0.1)',
                          left: `${Math.min(drawStart.x, drawCurrent.x)}px`,
                          top: `${Math.min(drawStart.y, drawCurrent.y)}px`,
                          width: `${Math.abs(drawCurrent.x - drawStart.x)}px`,
                          height: `${Math.abs(drawCurrent.y - drawStart.y)}px`,
                        }}
                      >
                        {pendingFieldType === 'QR_CODE' && (
                          <div className="absolute -top-6 left-0 text-xs bg-[#001A55]/80 text-white px-2 py-1 rounded">
                            {t('certificate.fieldMapping.square')}
                          </div>
                        )}
                      </div>
                    )}

                    {localMappings.filter((m) => (m.pageNumber ?? 1) === pageNumber).map((mapping) => {
                      const rect = pageRef.current?.getBoundingClientRect();
                      if (!rect || !pageDimensions) return null;

                      // Use pdf-lib dimensions if available, otherwise fallback to react-pdf dimensions
                      const dimensions = pdfLibDimensions || pageDimensions;
                      const ratio = pixelToPointRatio || (dimensions.width / rect.width);

                      const screenPageWidth = rect.width;
                      const screenPageHeight = (pageDimensions.height / pageDimensions.width) * screenPageWidth;
                      
                      // Convert PDF coordinates (points, bottom-left origin) to screen coordinates (pixels, top-left origin)
                      // X coordinate: divide by ratio
                      const screenX = Math.max(0, Math.min(mapping.x / ratio, screenPageWidth));
                      // Y coordinate: flip from bottom-left to top-left origin
                      const screenY = Math.max(0, Math.min((dimensions.height - mapping.y) / ratio, screenPageHeight));
                      
                      let screenWidth: number;
                      let screenHeight: number;
                      
                      if (mapping.width != null && mapping.height != null) {
                        // Convert PDF dimensions (points) to screen dimensions (pixels)
                        screenWidth = Math.max(20, mapping.width / ratio);
                        screenHeight = Math.max(20, mapping.height / ratio);
                        screenWidth = Math.min(screenWidth, screenPageWidth - screenX);
                        screenHeight = Math.min(screenHeight, screenPageHeight - screenY);
                      } else {
                        if (mapping.fieldType === 'QR_CODE') {
                          screenWidth = 100;
                          screenHeight = 100;
                        } else {
                          // Convert fontSize from PDF points to pixels for screen calculations
                          const pdfFontSize = mapping.fontSize || 16;
                          const screenFontSize = ratio ? pdfFontSize / ratio : pdfFontSize;
                          screenWidth = Math.max(50, Math.min(screenFontSize * 8, screenPageWidth - screenX));
                          screenHeight = Math.max(20, screenFontSize * 1.5);
                        }
                      }

                      return (
                        <ResizableField
                          key={mapping.id}
                          mapping={mapping}
                          screenX={screenX}
                          screenY={screenY}
                          screenWidth={screenWidth}
                          screenHeight={screenHeight}
                          pageDimensions={pageDimensions}
                          pdfLibDimensions={pdfLibDimensions}
                          pixelToPointRatio={pixelToPointRatio}
                          pageRect={rect}
                          onUpdate={(updates) => handleUpdateMapping(mapping.id, updates)}
                          onDelete={() => handleDeleteMapping(mapping.id)}
                          onEdit={() => setEditingMapping(mapping)}
                          getFieldTypeLabel={getFieldTypeLabel}
                          getPlaceholderText={getPlaceholderText}
                          isSelected={selectedMappingId === mapping.id}
                          onSelect={() => setSelectedMappingId(mapping.id)}
                        />
                      );
                    })}
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                    <p>{t('certificate.fieldMapping.loadingPdf')}</p>
                  </div>
                )}

                {pdfUrl && numPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                      disabled={pageNumber <= 1}
                    >
                      {t('certificate.fieldMapping.previous')}
                    </Button>
                    <span className="text-sm">
                      {t('certificate.fieldMapping.page')} {pageNumber} {t('certificate.fieldMapping.of')} {numPages}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                      disabled={pageNumber >= numPages}
                    >
                      {t('certificate.fieldMapping.next')}
                    </Button>
                  </div>
                )}

                {pdfUrl && (
                  <div className="flex justify-center items-center gap-2 mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => adjustZoom(-ZOOM_STEP)}
                      disabled={zoom <= ZOOM_MIN}
                      title={t('certificate.fieldMapping.zoomOut')}
                    >
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                    <span className="text-sm w-14 text-center">
                      {Math.round(zoom * 100)}%
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => adjustZoom(ZOOM_STEP)}
                      disabled={zoom >= ZOOM_MAX}
                      title={t('certificate.fieldMapping.zoomIn')}
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setZoom(1)}
                      disabled={zoom === 1}
                      title={t('certificate.fieldMapping.resetZoom')}
                    >
                      <Maximize className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-medium mb-2">{t('certificate.fieldMapping.mappedFields')} ({localMappings.length})</h3>
                {localMappings.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('certificate.fieldMapping.noFieldsMapped')}</p>
                ) : (
                  <div className="space-y-2">
                    {localMappings.map((mapping) => (
                      <div
                        key={mapping.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div>
                          <div className="font-medium">{getPlaceholderText(mapping)}</div>
                          <div className="text-xs text-gray-500">
                            {getFieldTypeLabel(mapping.fieldType)} — {t('certificate.fieldMapping.position')}: ({Math.round(mapping.x)}, {Math.round(mapping.y)})
                            {numPages > 1 && (
                              <span className="ml-2">
                                — {t('certificate.fieldMapping.page')} {mapping.pageNumber ?? 1}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteMapping(mapping.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Field Configuration Modal */}
      <FieldConfigurationModal
        isOpen={!!editingMapping}
        onClose={() => setEditingMapping(null)}
        mapping={editingMapping}
        processId={processId}
        onSave={async (updates) => {
          if (editingMapping) {
            await handleUpdateMapping(editingMapping.id, updates);
          }
        }}
      />
    </div>
  );
};

export default CertificateFieldMapper;

